import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SLIP_STYLE = { overdue: "text-red-800", at_risk: "text-amber-800",
                     on_track: "text-emerald-800", done: "text-muted-foreground",
                     no_date: "text-amber-800", unscheduled: "text-amber-800" };
const SLIP_LABEL = { overdue: "overdue", at_risk: "at risk", on_track: "on track",
                     done: "done", no_date: "no date", unscheduled: "unscheduled" };

export default function Projects() {
  const [board, setBoard] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [crunch, setCrunch] = useState(null);
  const [selected, setSelected] = useState(null);
  const [create, setCreate] = useState({ name: "", template_key: "" });
  const [item, setItem] = useState({ title: "", group_key: "", owner: "", due: "" });
  const [member, setMember] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/projects/board").then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Projects are not available to you"); return; }
      setError(null);
      setBoard(body);
      setCreate((c) => ({ ...c, template_key: c.template_key || body.templates[0]?.key || "" }));
      setSelected((s) => (s ? body.projects.find((p) => p.id === s.id) || null : null));
    });
    apiFetch("/projects/timeline?weeks=8").then(async (r) => {
      if (r.ok) setTimeline(await r.json());
    });
    apiFetch("/projects/crunch-warnings").then(async (r) => {
      if (r.ok) setCrunch(await r.json());
    });
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load();
  }, [navigate, load]);

  const post = (path, body, label, method = "POST") =>
    apiFetch(path, { method, body: JSON.stringify(body) }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(`${label} failed — ${typeof out.detail === "string" ? out.detail
                                                                       : JSON.stringify(out.detail)}`);
        return false;
      }
      setMessage(`${label} done`);
      load();
      return true;
    });

  const addItem = async () => {
    const values = {};
    if (item.due && selected.due_column) values[selected.due_column] = item.due;
    const ok = await post(`/projects/${selected.id}/items`,
                          { title: item.title, group_key: item.group_key || null, values,
                            owner_external_id: item.owner || null }, "Item added");
    if (ok) setItem({ title: "", group_key: "", owner: "", due: "" });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="projects-title">Projects</h1>
          <p className="text-sm text-muted-foreground">
            Plan the work, name an owner, and see what is slipping.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="projects-queue-link">
            <Link to="/queue">Queue</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="projects-error">{error}</p>}
        {message && <p className="text-sm text-amber-800" data-testid="projects-message">{message}</p>}

        {board && (
          <>
            <section className="grid gap-3 md:grid-cols-4 text-sm" data-testid="projects-totals">
              {[["Projects", board.totals.projects, "projects"],
                ["Slipping items", board.totals.slipping, "slipping"],
                ["Overdue", board.totals.overdue, "overdue"],
                ["At risk", board.totals.at_risk, "at-risk"]].map(([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`projects-total-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            <section className="space-y-2" data-testid="projects-slipping">
              <h2 className="text-base md:text-lg font-medium">What is slipping</h2>
              <p className="text-sm text-muted-foreground" data-testid="projects-method">
                {board.method}
              </p>
              <ul className="space-y-1 text-sm">
                {board.slipping.map((row) => (
                  <li key={row.id} data-testid={`projects-slip-${row.id}`}>
                    <span className={SLIP_STYLE[row.slip.state]}>
                      {SLIP_LABEL[row.slip.state]}
                    </span>{" "}
                    — {row.title} ({row.project_name}) ·{" "}
                    {row.owner ? row.owner.email : "nobody owns it"} · {row.slip.reason}
                  </li>
                ))}
                {board.slipping.length === 0 && (
                  <li className="text-muted-foreground" data-testid="projects-slipping-empty">
                    Nothing is slipping right now.
                  </li>
                )}
              </ul>
              {Object.keys(board.slipping_by_owner).length > 0 && (
                <p className="text-sm" data-testid="projects-slipping-by-owner">
                  By owner:{" "}
                  {Object.entries(board.slipping_by_owner)
                    .map(([who, n]) => `${who}: ${n}`).join(" · ")}
                </p>
              )}
            </section>

            {timeline && (
              <section className="space-y-2 border-t pt-6" data-testid="projects-timeline">
                <h2 className="text-base md:text-lg font-medium">
                  Timeline — the next {timeline.window_weeks} weeks
                </h2>
                <p className="text-sm text-muted-foreground" data-testid="projects-timeline-method">
                  {timeline.method}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border rounded-md">
                    <caption className="sr-only">Project items by week</caption>
                    <thead>
                      <tr className="text-left border-b">
                        <th scope="col" className="px-3 py-2">Week of</th>
                        <th scope="col" className="px-3 py-2">Items due</th>
                        <th scope="col" className="px-3 py-2">By owner</th>
                        <th scope="col" className="px-3 py-2">Crunch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.weeks.map((week) => (
                        <tr key={week.week_start} className="border-b last:border-0"
                            data-testid={`timeline-week-${week.week_start}`}>
                          <th scope="row" className="px-3 py-2 text-left font-medium">
                            {week.week_start}
                          </th>
                          <td className="px-3 py-2">
                            <span className="inline-block h-2 rounded bg-foreground/70 align-middle mr-2"
                                  style={{ width: `${Math.min(week.total, 12) * 10}px` }} />
                            {week.total}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {Object.entries(week.by_owner)
                              .map(([who, n]) => `${who}: ${n}`).join(" · ") || "—"}
                          </td>
                          <td className={`px-3 py-2 ${week.crunch ? "text-red-800" : "text-muted-foreground"}`}
                              data-testid={`timeline-crunch-${week.week_start}`}>
                            {week.crunch ? week.crunch_reason : "clear"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="projects-timeline-outside">
                  {timeline.totals.crunch_weeks} crunch week(s) ahead ·{" "}
                  {timeline.outside_window.before_this_week} item(s) already past,{" "}
                  {timeline.outside_window.after_the_window} beyond the window,{" "}
                  {timeline.outside_window.no_readable_date} with no readable date.
                </p>
                {crunch && (
                  <div className="space-y-1" data-testid="projects-crunch-warnings">
                    <h3 className="text-sm font-semibold">
                      Warnings for the week of {crunch.week_start}
                    </h3>
                    <p className="text-xs text-muted-foreground"
                       data-testid="projects-crunch-method">
                      {crunch.method} · {crunch.schedule}
                    </p>
                    <ul className="text-sm space-y-1">
                      {crunch.warnings.map((w) => (
                        <li key={w.owner} data-testid={`projects-crunch-${w.owner}`}>
                          <span className="text-amber-800">{w.owner}</span> — {w.items} items due
                          across {w.projects.join(", ")}
                          {w.managers.length > 0 && ` · manager ${w.managers.join(", ")}`}
                          {w.already_warned ? " · already warned" : " · not warned yet"}
                        </li>
                      ))}
                      {crunch.warnings.length === 0 && (
                        <li className="text-muted-foreground"
                            data-testid="projects-crunch-empty">
                          Nobody is heading into a crowded week.
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {board.can_create && (
              <section className="space-y-2 border-t pt-6" data-testid="projects-create">                <h2 className="text-base md:text-lg font-medium">Start a project</h2>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <label htmlFor="project-name" className="text-sm font-medium">Name</label>
                    <Input id="project-name" data-testid="project-name" value={create.name}
                           onChange={(e) => setCreate({ ...create, name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="project-template" className="text-sm font-medium">
                      Template
                    </label>
                    <select id="project-template" data-testid="project-template"
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            value={create.template_key}
                            onChange={(e) => setCreate({ ...create,
                                                         template_key: e.target.value })}>
                      {board.templates.map((t) => (
                        <option key={t.key} value={t.key}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" data-testid="project-create-submit"
                            disabled={!create.name || !create.template_key}
                            onClick={async () => {
                              const ok = await post("/projects", create, "Project created");
                              if (ok) setCreate({ ...create, name: "" });
                            }}>
                      Create project
                    </Button>
                  </div>
                </div>
              </section>
            )}

            <section className="space-y-2 border-t pt-6" data-testid="projects-list">
              <h2 className="text-base md:text-lg font-medium">Your projects</h2>
              <ul className="space-y-1">
                {board.projects.map((p) => (
                  <li key={p.id}>
                    <button className="w-full text-left text-sm border rounded px-3 py-2 hover:border-foreground/40"
                            data-testid={`project-row-${p.number}`} onClick={() => setSelected(p)}>
                      <span className="font-mono">{p.number}</span> · {p.name} ·{" "}
                      <span className="text-muted-foreground">
                        {p.items.length} item(s), {p.slipping} slipping
                      </span>
                    </button>
                  </li>
                ))}
                {board.projects.length === 0 && (
                  <li className="text-sm text-muted-foreground" data-testid="projects-list-empty">
                    You are not on any project yet.
                  </li>
                )}
              </ul>
            </section>
          </>
        )}

        {selected && (
          <section className="space-y-4 border-t pt-6" data-testid="project-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="project-detail-title">
                {selected.number} — {selected.name}
              </h2>
              <Button size="sm" variant="ghost" data-testid="project-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="project-detail-meta">
              managed by {selected.manager?.email || "unknown"} ·{" "}
              {selected.members.length} member(s) · template {selected.template_key}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">Project items</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Item</th>
                    <th scope="col" className="px-3 py-2">Owner</th>
                    {selected.columns.map((c) => (
                      <th key={c.key} scope="col" className="px-3 py-2">{c.label}</th>
                    ))}
                    <th scope="col" className="px-3 py-2">State</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.items.map((it) => (
                    <tr key={it.id} className="border-b last:border-0"
                        data-testid={`project-item-${it.id}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium">
                        {it.title}
                        {selected.subitems.filter((s) => s.parent_item_id === it.id).length > 0 && (
                          <span className="block text-xs text-muted-foreground">
                            {selected.subitems.filter((s) => s.parent_item_id === it.id).length}{" "}
                            subitem(s)
                          </span>
                        )}
                      </th>
                      <td className="px-3 py-2">
                        <select aria-label={`Owner of ${it.title}`}
                                data-testid={`project-item-owner-${it.id}`}
                                className="border rounded-md px-2 py-1 text-sm bg-background"
                                value={it.owner_external_id || ""}
                                onChange={(e) => post(`/projects/items/${it.id}`,
                                                       { owner_external_id: e.target.value,
                                                         version: it.version },
                                                       "Owner set", "PUT")}>
                          <option value="">Nobody</option>
                          {board.people.map((p) => (
                            <option key={p.external_id} value={p.external_id}>{p.email}</option>
                          ))}
                        </select>
                      </td>
                      {selected.columns.map((c) => (
                        <td key={c.key} className="px-3 py-2">
                          {c.type === "select" || c.type === "status" ? (
                            <select aria-label={`${c.label} for ${it.title}`}
                                    data-testid={`project-item-${c.key}-${it.id}`}
                                    className="border rounded-md px-2 py-1 text-sm bg-background"
                                    value={it.values[c.key] || ""}
                                    onChange={(e) => post(`/projects/items/${it.id}`,
                                                           { values: { [c.key]: e.target.value },
                                                             version: it.version },
                                                           `${c.label} set`, "PUT")}>
                              <option value="">—</option>
                              {(c.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input aria-label={`${c.label} for ${it.title}`}
                                   data-testid={`project-item-${c.key}-${it.id}`}
                                   type={c.type === "date" ? "date"
                                         : c.type === "number" ? "number" : "text"}
                                   className="border rounded-md px-2 py-1 text-sm bg-background w-32"
                                   defaultValue={it.values[c.key] ?? ""}
                                   onBlur={(e) => {
                                     const raw = e.target.value;
                                     if (String(it.values[c.key] ?? "") === raw) return;
                                     post(`/projects/items/${it.id}`,
                                          { values: { [c.key]: c.type === "number"
                                                        ? Number(raw) : raw },
                                            version: it.version }, `${c.label} set`, "PUT");
                                   }} />
                          )}
                        </td>
                      ))}
                      <td className={`px-3 py-2 ${SLIP_STYLE[it.slip.state]}`}
                          data-testid={`project-item-slip-${it.id}`}>
                        {SLIP_LABEL[it.slip.state]}
                        <span className="block text-xs text-muted-foreground">
                          {it.slip.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {selected.items.length === 0 && (
                    <tr><td colSpan={selected.columns.length + 3}
                            className="px-3 py-2 text-muted-foreground"
                            data-testid="project-items-empty">
                      No items yet — add the first piece of work below.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="border rounded-md p-4 space-y-2" data-testid="project-add-item">
                <h3 className="text-sm font-semibold">Add an item</h3>
                <Input placeholder="What needs doing" value={item.title}
                       data-testid="project-item-title"
                       onChange={(e) => setItem({ ...item, title: e.target.value })} />
                {selected.groups.length > 0 && (
                  <select aria-label="Group" data-testid="project-item-group"
                          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                          value={item.group_key}
                          onChange={(e) => setItem({ ...item, group_key: e.target.value })}>
                    <option value="">No group</option>
                    {selected.groups.map((g) => (
                      <option key={g.key} value={g.key}>{g.label || g.key}</option>
                    ))}
                  </select>
                )}
                <select aria-label="Owner" data-testid="project-item-new-owner"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={item.owner}
                        onChange={(e) => setItem({ ...item, owner: e.target.value })}>
                  <option value="">Nobody yet</option>
                  {board.people.map((p) => (
                    <option key={p.external_id} value={p.external_id}>{p.email}</option>
                  ))}
                </select>
                {selected.due_column && (
                  <Input type="date" aria-label="Due date" value={item.due}
                         data-testid="project-item-new-due"
                         onChange={(e) => setItem({ ...item, due: e.target.value })} />
                )}
                <Button size="sm" variant="outline" data-testid="project-item-add"
                        disabled={!item.title} onClick={addItem}>
                  Add item
                </Button>
              </div>

              <div className="border rounded-md p-4 space-y-2" data-testid="project-add-member">
                <h3 className="text-sm font-semibold">Add a member</h3>
                <Input placeholder="Their email" value={member} data-testid="project-member-email"
                       onChange={(e) => setMember(e.target.value)} />
                <Button size="sm" variant="outline" data-testid="project-member-add"
                        onClick={async () => {
                          const ok = await post(`/projects/${selected.id}/members`,
                                                { email: member }, "Member added");
                          if (ok) setMember("");
                        }}>
                  Add member
                </Button>
                <ul className="text-sm space-y-1" data-testid="project-members">
                  {selected.members.map((m, i) => (
                    <li key={i} className="text-muted-foreground">{m?.email || m?.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
